// Loading the bar, in time: WHEN each part of a plate change moves.
//
// BarSleeve does the drawing and the DOM; this file is only the clock, so the
// timings can be read, tested and changed in one place. Nothing here touches
// the page.
//
// WHAT A LIFTER DOES, and so what the drawing does:
//
//   1. The collar comes off, slid along the sleeve and set aside.
//   2. Plates coming off go first, the outermost first — slowly along the
//      sleeve, then away.
//   3. Plates going on follow, the innermost first — fast through the air, slow
//      along the sleeve, and a clunk as each one seats.
//   4. The collar goes back on, against the new last plate.
//
// An EMPTY BAR HAS NO COLLAR ON IT, so loading the first plate skips step 1
// and taking the last plate off skips step 4.
//
// FAST, because it plays on every tap of Count mode. One plate is about half a
// second end to end; however many plates change, the whole thing is held to
// `max`. Past that the gaps between plates close up first and then everything
// runs faster together, so a big change still reads as the same gesture.

export const TIMING = {
  collarOff: 110,  // the collar pulled off to past the sleeve tip
  offLead: 50,     // the outer plate starts once the collar is moving
  off: 300,        // one plate slid off and carried away
  offGap: 60,      // between plates coming off
  offSplit: 0.55,  // the share of `off` spent sliding along the sleeve
  handover: 1,     // plates go on once the last one off has left the screen: they share one path, and must not pass through each other
  onLead: 70,      // the first plate on waits for the collar to be clear
  on: 340,         // one plate in through the air and slid to its seat
  onGap: 70,       // between plates going on
  onSplit: 0.4,    // the share of `on` spent in the air: most of the distance
  seat: 0.9,       // the moment it lands (the clunk), as a share of `on`
  collarBack: 0.72, // after a removal, the collar comes back this far into the last plate's exit
  collarOn: 130,   // the collar slid back on
  max: 880,        // the ceiling, however many plates
  gapFloor: 0.5,   // how far the gaps may close before everything speeds up
}

/** How many plates, from the inside, the two stacks have in common. */
export function sharedPrefix(a, b) {
  let n = 0
  while (n < a.length && n < b.length && Number(a[n]) === Number(b[n])) n += 1
  return n
}

/**
 * The timeline for one change of the bar, from `before` to `after` (weights per
 * side, inside out). `collarAway` says the collar is already off — the previous
 * change was cut short part-way — so this one does not take it off again.
 *
 * Returns null when nothing moves. Every time is in ms from the start:
 *   offs    [{ i, start, dur }]          i indexes `before`; outermost first
 *   ons     [{ i, start, dur, seatAt }]  i indexes `after`; innermost first
 *   collar  { off: {start,end}|null, on: {start,end}|null }
 *   reveal  when the "new" / "off" words and the dashed outlines fade in
 *   end     when everything is where the still drawing has it
 */
export function choreograph(before, after, { collarAway = false, timing = TIMING } = {}) {
  const T = timing
  const n = sharedPrefix(before, after)
  const offIdx = []
  for (let i = before.length - 1; i >= n; i -= 1) offIdx.push(i)
  const onIdx = []
  for (let i = n; i < after.length; i += 1) onIdx.push(i)
  if (!offIdx.length && !onIdx.length) return null

  const collarOff = before.length > 0 && !collarAway
  const collarOn = after.length > 0

  const lay = (gap, speed) => {
    const s = (ms) => ms * speed
    const offs = offIdx.map((i, k) => ({
      i,
      start: s((collarOff ? T.offLead : 0) + k * T.offGap * gap),
      dur: s(T.off),
    }))
    const lastOff = offs[offs.length - 1]
    const onFrom = lastOff
      ? lastOff.start + s(T.handover * T.off)
      : s(collarOff ? T.onLead : 0)
    const ons = onIdx.map((i, k) => {
      const start = onFrom + s(k * T.onGap * gap)
      return { i, start, dur: s(T.on), seatAt: start + s(T.seat * T.on) }
    })
    const lastOn = ons[ons.length - 1]
    let back = null
    if (collarOn) {
      const start = lastOn ? lastOn.seatAt + s(10) : lastOff.start + s(T.collarBack * T.off)
      back = { start, end: start + s(T.collarOn) }
    }
    const end = Math.max(
      lastOff ? lastOff.start + lastOff.dur : 0,
      lastOn ? lastOn.start + lastOn.dur : 0,
      back ? back.end : 0,
      collarOff ? s(T.collarOff) : 0,
    )
    return {
      n,
      offs,
      ons,
      collar: { off: collarOff ? { start: 0, end: s(T.collarOff) } : null, on: back },
      reveal: back ? back.start : Math.max(0, end - s(120)),
      end,
    }
  }

  // Natural timing first; over the ceiling, close the gaps (they are what
  // grows with the plate count), and only then speed the whole thing up.
  let plan = lay(1, 1)
  if (plan.end > T.max) {
    const tight = lay(T.gapFloor, 1)
    const g = plan.end === tight.end ? 1 : Math.max(T.gapFloor, 1 - ((1 - T.gapFloor) * (plan.end - T.max)) / (plan.end - tight.end))
    plan = lay(g, 1)
    if (plan.end > T.max + 0.5) plan = lay(g, T.max / plan.end)
  }
  return plan
}
